import React, { useEffect, useRef, useState } from 'react'
import injectSheet from 'react-jss'
import { apiClient } from 'pobble-components/dist/api'
import { useParams } from 'react-router-dom'
import { Presentation } from 'pobble-components/dist/presentation'
import logo from './logo.svg'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExpandAlt } from '@fortawesome/pro-regular-svg-icons/faExpandAlt'
import ButtonElement from 'pobble-components/dist/button-element'
import { usePresentMode } from '../direct-present/present-mode'
import CtaModal from './cta-modal'
import posthog from 'posthog-js'

export const TeacherPreview = injectSheet({
  container: {
    minHeight: '100vh',
    background: '#191919',
    overflow: 'hidden',
    padding: '24px'
  },
  skeleton: {
    maxWidth: '960px',
    margin: '0 auto'
  },
  bone: {
    paddingBottom: '56%',
    background: '#1f1f1f',
    borderRadius: '8px',
    marginTop: '24px',
    '&:first-child': {
      marginTop: '0'
    }
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    color: 'var(--light-gray)',
    gap: '8px',
    marginBottom: '16px',
    '& p': {
      margin: '0',
      transform: 'translate(0, -8px)',
      opacity: '0',
      animation: '.8s appear .4s ease-out forwards'
    }
  },
  slidesContainer: {
    maxWidth: '960px',
    margin: '0 auto'
  },
  slide: {
    borderRadius: '8px',
    transform: 'translateZ(0)',
    background: '#1f1f1f',
    overflow: 'hidden',
    marginTop: '24px',
    position: 'relative',
    '&:first-child': {
      marginTop: '0'
    }
  },
  slideHolder: {
    opacity: '0',
    animation: 'appear .3s ease-out forwards'
  },
  logo: {
    display: 'block',
    width: '80px',
    margin: '0 auto',
    height: '46px',
    transform: 'translate(0, -8px)',
    opacity: '0',
    animation: 'appear .8s ease-out forwards',
    '& img': {
      width: '100%'
    }
  },
  fullScreenButton: {
    fontSize: '28px',
    position: 'absolute',
    right: '0',
    top: '0',
    padding: '16px',
    color: 'rgba(255, 255, 255, .7)',
    '& svg': {
      stroke: '#000',
      strokeWidth: '4px',
      strokeOpacity: '.5'
    }
  },
  '@keyframes appear': {
    to: {
      opacity: '1',
      transform: 'translate(0, 0)'
    }
  }
})(({ classes }) => {
  const [presentation, setPresentation] = useState(null)
  const { id } = useParams()
  const slidesContainerRef = useRef(null)
  const [presentationReady, setReady] = useState(false)
  const { present } = usePresentMode()

  posthog.capture('landed on teacher preview', {
    url: window.location.href
  })

  useEffect(
    () => {
      if (presentation) {
        document.title = `${presentation.title} - Pobble`
      }
    },
    [presentation]
  )

  /**
   * Preload the first image of the presentation
   *  so that users don't see progressive image laoding
   */
  useEffect(
    () => {
      let timer = null
      let ready = false

      const setAsReady = () => {
        if (!ready) {
          setReady(true)
          ready = true
        }
      }

      if (presentation) {
        const image =
          !presentation.asset && presentation.image && presentation.image.url

        if (!image) {
          setAsReady()
        } else {
          const img = new Image()
          img.src = image
          img.onload = () => setAsReady()

          /**
           * 3 second fallback in cases when image doesn't load
           */
          timer = setTimeout(() => setAsReady(), 3000)
        }
      }

      return () => {
        clearTimeout(timer)
      }
    },
    [presentation]
  )

  const DEFAULT_INCLUDE =
    'presentation.p365,presentation.account,presentation.category,presentation.status,presentation.image,presentation.year_groups,presentation.year_group.order,presentation.details,presentation.slides,presentation.slide.type,presentation.slide.details,presentation.names'

  useEffect(
    () => {
      setPresentation(null)
      if (id !== 'today') {
        apiClient
          .get(`/v3/presentations/${id}`, {
            params: {
              include: DEFAULT_INCLUDE
            }
          })
          .then(res => setPresentation(res.data.presentation))
      } else {
        apiClient
          .get(`/v3/p365/today`, {
            params: {
              count: 1,
              from: 0,
              include: DEFAULT_INCLUDE
            }
          })
          .then(res => setPresentation(res.data.presentations[0]))
      }
    },
    [id]
  )

  return (
    <>
      <div className={classes.container}>
        <header className={classes.header}>
          <a
            className={classes.logo}
            target="_blank"
            rel="noopener noreferrer"
            href="https://pobble.com">
            <img src={logo} alt="Pobble" />
          </a>
          <p>Inspiring young writers!</p>
        </header>
        {!presentationReady && (
          <div className={classes.skeleton}>
            <div className={classes.bone} />
            <div className={classes.bone} />
            <div className={classes.bone} />
            <div className={classes.bone} />
          </div>
        )}
        {presentationReady && (
          <div ref={slidesContainerRef} className={classes.slidesContainer}>
            <Presentation
              containerRef={slidesContainerRef}
              multiSlides
              data={presentation}>
              {({ slides }) => {
                return slides.map((slide, i) => {
                  return (
                    <>
                      <div className={classes.slide}>
                        <div className={classes.slideHolder}>
                          {slide}
                          {i === 0 && (
                            <>
                              <ButtonElement
                                onClick={() => present(presentation.id)}
                                className={classes.fullScreenButton}>
                                <FontAwesomeIcon icon={faExpandAlt} />
                              </ButtonElement>
                            </>
                          )}
                        </div>
                      </div>
                    </>
                  )
                })
              }}
            </Presentation>
          </div>
        )}
      </div>
      <CtaModal />
    </>
  )
})
